import SearchService, { SearchServiceOptions } from './SearchService';
import { API_ROUTES } from 'lib/workperks/common/constants';
import { axios } from "lib/Init";
import ESPerkMapper from '../models/ESPerkMapper';
import pluralize from 'pluralize';

const isPresent = value => {
    if (!value) {
        return false;
    }

    if (typeof value === 'string' || Array.isArray(value)) {
        return value.length > 0;
    }

    return true;
};

const EXPIRATION_THRESHOLD = 10;
const NEW_THRESHOLD = 30;

export default class ESSponsoredSearchService extends SearchService {
    filterMethods = null;
    sortMethods = null;

    constructor({ user, location, language, resultSize = 10 }: SearchServiceOptions) {
        super({ user, location, language, resultSize });
        this.filterMethods = this.initFilters();
        this.sortMethods = this.initSort();

        if (this.language === 'fr') {
            this._addFrPluralizeRules();
        }
    }

    protected createPerkMapper() {
        return new ESPerkMapper(this.impressionDescriptor());
    }

    _getFilters() {
        let filters: any[] = [];

        if (!isPresent(this.showOnlineOnly) && this.redemptionType && this.redemptionType.length > 0) {
            filters.push(this.filterMethods.redemptionType(this.redemptionType));
        }

        // DEFAULT FILTERS
        // filters.push(this.filterMethods.language());
        filters.push(...this.filterMethods.campaignActive());
        filters.push(this.filterMethods.site(window.s.site.restrictedCategories));
        filters.push(this.filterMethods.restrictedAccounts(window.s.site.restrictedAccounts));
        filters.push(this.filterMethods.active());
        filters.push(this.filterMethods.date());
        isPresent(window.s.site.express) && filters.push(this.filterMethods.express());

        if (isPresent(this.showNew) && isPresent(this.showExpiringSoon)) {
            filters.push(this.filterMethods.expiringSoonAndNew());
        } else if (isPresent(this.showNew)) {
            filters.push(this.filterMethods.new());
        } else if (isPresent(this.showExpiringSoon)) {
            filters.push(this.filterMethods.expiringSoon());
        }

        isPresent(this.user.licenses) && filters.push(this.filterMethods.license());

        isPresent(this.user.parent_id) && filters.push(this.filterMethods.child());

        // SEARCH FILTERS
        // Change into a foreach? Or let every filter method decide
        // whether it gets pushed...
        isPresent(this.categoryId) && filters.push(this.filterMethods.category(this.categoryId));
        isPresent(this.subcategoryId) && filters.push(this.filterMethods.subcategory(this.subcategoryId));
        isPresent(this.location) && this.distance > 0 && filters.push(this.filterMethods.location());
        isPresent(this.visibleLocations) &&
            this.visibleLocations === true &&
            filters.push(this.filterMethods.visibleLocations());
        // !isPresent(this.showOnlineOnly) && isPresent(this.redemptionType) &&
        //   filters.push(this.filterMethods.redemptionType());
        isPresent(this.showOnlineOnly) && filters.push(this.filterMethods.onlineOnly());
        isPresent(this.tier) && filters.push(this.filterMethods.tier(this.tier));
        isPresent(this.ids) && filters.push(this.filterMethods.ids(this.ids));
        isPresent(this.label) && filters.push(this.filterMethods.label(this.label));
        isPresent(this.keyword) && filters.push(this.filterMethods.keyword(this.keyword));
        isPresent(this.account) && filters.push(this.filterMethods.account(this.account));
        return filters;
    }

    _addFrPluralizeRules() {
        pluralize.addIrregularRule('oeuil', 'oeux');
        pluralize.addIrregularRule('ciel', 'cieux');
        pluralize.addIrregularRule('ail', 'aulx');
        pluralize.addIrregularRule('bail', 'baux');
        pluralize.addIrregularRule('bal', 'bals');
        pluralize.addIrregularRule('pal', 'pals');
        pluralize.addIrregularRule('val', 'vals');
        pluralize.addIrregularRule('eau', 'eaux');

        pluralize.addPluralRule(/(eau|au|eu)$/i, '$1x');
        pluralize.addSingularRule(/(eau|au|eu|ou)(x|s)$/i, '$1');

        pluralize.addPluralRule(/(bleu|émeu|landau|pneu|sarrau)$/i, '$1s');
        pluralize.addPluralRule(/(bijou|caillou|chou|genou|hibou|joujou|pou)$/i, '$1x');

        pluralize.addPluralRule(/(al)$/i, 'aux');
        pluralize.addPluralRule(/(cor|ém|gemm|soupir|trav|vant|vitr)ail$/i, '$1aux');
        pluralize.addPluralRule(/(av|ban|banc|carnav|cérémoni|chor|ét|fat|festiv|nat|nav|récit|rég|ton|virgin)al$/i, '$1als');
        pluralize.addSingularRule(/(cor|ém|gemm|soupir|trav|vant|vitr)aux$/i, '$1ail');
        pluralize.addSingularRule(/(anim|chev|journ|can|tribun|sign|mur)aux$/i, '$1al');

        pluralize.addPluralRule(/(?:s|x|z)$/i, '$0');

        ['anglais', 'avis', 'bras', 'bus', 'choix', 'colis', 'cours', 'foie-gras', 'faux', 'français', 'gaz', 'hors-d’œuvre', 'mois', 'nez', 'paradis', 'pas', 'pays', 'poids', 'prix', 'procès', 'quiz', 'repas', 'riz', 'sens', 'tapis', 'temps', 'virus'].forEach(pluralize.addUncountableRule);

    }

    _getQueries(term: string): string[] {
        const singular = pluralize.singular(term);
        const plural = pluralize.plural(term);
        return [
            singular,
            plural
        ];
    }

    _queryObject(query: string) {
        if (!query || query.length === 0) {
            return { match_all: {} };
        }
        const [singular, plural] = this._getQueries(query);

        return {
            bool: {
                should: [
                    {
                        wildcard: {
                            'sponsoredKeywordTerms.keyword': {
                                value: singular + '*',
                                case_insensitive: true,
                            },
                        },
                    },
                    {
                        wildcard: {
                            'sponsoredKeywordTerms.keyword': {
                                value: plural + '*',
                                case_insensitive: true,
                            },
                        },
                    },
                ],
                minimum_should_match: 1,
            },
        };
    }

    _buildRequest(query) {
        let queryObject = this.template ? this.template : this._queryObject(query);
        let filters = this._getFilters();

        return {
            size: this.resultSize,
            from: this.offset,
            _source: this.esSource,
            query: {
                bool: {
                    must: [queryObject],
                    filter: {
                        bool: {
                            must: filters,
                        },
                    },
                },
            },
            sort: [
                {
                    "sponsoredKeywordOrder.keyword": {
                        "order": "asc"
                    }
                }
            ],
        };
    }

    async search(query?) {
        query = query && query.length > 0 ? query.toLowerCase() : null;
        super.search(query);
        let request = this._buildRequest(query);

        // Prod will return 404 if there is an error, don't panic
        // thinking it's the URL...
        return axios
            .post(API_ROUTES.elasticsearch.perk, request)
            .then(response => {
                this._lastTotalHits = response.data.hits.total.value;
                return response.data.hits.hits.map(perk => perk._source);
            })
            .catch(response => {
                console.error("Error-----Res", response);
                this._lastTotalHits = 0;
            });
    }

    initSort() {
        var alphaSort = {};
        alphaSort[`title.lower_case_sort`] = {
            order: 'asc',
        };
        return {
            // relevance: () => ({ _score: { order: 'desc' } }),
            distance: () => ({
                _geo_distance: {
                    'locations.pin': [
                        parseFloat(this.location.longitude),
                        parseFloat(this.location.latitude),
                    ],
                    order: 'asc',
                    unit: this.distanceUnit,
                    mode: 'min',
                    distance_type: 'arc',
                },
            }),
            alpha: () => alphaSort,
            date: () => ({ statusChanged: { order: 'desc' } }),
            tier: () => ({ tier: { order: 'asc' } }),
        };
    }

    initFilters() {
        // @ts-ignore
        return {
            active: () => ({ term: { statusId: 5 } }),
            license: () => {
                const licenses = this.user.licenses.filter((license) =>
                    this.location.country === 'US' ? parseInt(license) !== 1 : parseInt(license) !== 2,
                );
                return { terms: { licenses: licenses ?? [1] } };
            },
            // Below Not needed as ES is localized
            // language: () => ({
            //     terms: { lang_filter: this.language == 'en' ? [1] : [2] },
            // }),
            category: category => ({ term: { 'category.id': category } }),
            subcategory: subcategory => ({
                terms: { 'subcategory.id': subcategory },
            }),
            date: () => ({
                bool: {
                    should: [
                        {
                            bool: {
                                must: [
                                    { term: { neverExpiring: 1 } },
                                    {
                                        range: {
                                            validFrom: {
                                                lte: 'now/d',
                                            },
                                        },
                                    },
                                ]
                            }
                        },
                        {
                            bool: {
                                must: [
                                    { term: { neverExpiring: 0 } },
                                    {
                                        range: {
                                            validFrom: {
                                                lte: 'now/d',
                                            },
                                        },
                                    },
                                    {
                                        range: {
                                            validTo: {
                                                gte: 'now/d',
                                            },
                                        },
                                    }
                                ]
                            }
                        }
                    ]
                }
            }),
            campaignActive: () => [
                {
                    range: {
                        'campaign.startDate': {
                            lte: 'now/d',
                        },
                    },
                },
                {
                    range: {
                        'campaign.endDate': {
                            gte: 'now/d',
                        },
                    },
                },
                {
                    term: {
                        'campaign.statusId': 1
                    }
                }
            ],
            tier: tier => ({
                term: { tier: tier },
            }),
            visibleLocations: () => ({ term: { 'location.visible': 1 } }),
            expiringSoon: () => ({
                bool: {
                    must: {
                        range: {
                            validTo: {
                                gte: 'now/d',
                                lte: 'now+' + EXPIRATION_THRESHOLD + 'd/d',
                            },
                        },
                    },
                },
            }),
            expiringSoonAndNew: () => ({
                bool: {
                    should: [
                        {
                            bool: {
                                must: {
                                    range: {
                                        validTo: {
                                            gte: 'now/d',
                                            lte: 'now+' + EXPIRATION_THRESHOLD + 'd/d',
                                        },
                                    },
                                },
                            },
                        },
                        {
                            bool: {
                                should: [
                                    {
                                        bool: {
                                            must: {
                                                match: {
                                                    isNew: 1
                                                }
                                            }
                                        }
                                    },
                                    {
                                        bool: {
                                            must: {
                                                range: {
                                                    validFrom: {
                                                        gt: 'now-' + NEW_THRESHOLD + 'd/d',
                                                        lte: 'now/d',
                                                    },
                                                },
                                            }
                                        },
                                    }
                                ],
                            },
                        }

                    ],
                },
            }),
            keyword: keyword => ({
                bool: {
                    must: {
                        match: { 'keywords.id': keyword },
                    },
                },
            }),
            label: label => ({
                bool: {
                    must: {
                        match: { 'labelIds': label },
                    },
                },
            }),
            account: account => ({
                bool: {
                    must: {
                        match: { 'accountId': account },
                    },
                },
            }),
            restrictedAccounts: accountIds => ({
                bool: {
                    must_not: {
                        terms: { 'accountId': accountIds },
                    },
                },
            }),
            // Needs to be aware of express
            site: (categoryList) => ({
                bool: {
                    should: [
                        {
                            bool: {
                                should: [
                                    { term: { restrictionType: 'Globally Available' } }
                                ],
                                must_not: [
                                    { terms: { 'category.id': categoryList } },
                                    { terms: { 'subcategory.id': categoryList } }
                                ]
                            },
                        },
                        {
                            bool: {
                                should: [
                                    { term: { restrictionType: 'Hidden' } },
                                ],
                                must_not: [
                                    { term: { restrictedSites: window.s.site.id } },
                                    { terms: { 'category.id': categoryList } },
                                    { terms: { 'subcategory.id': categoryList } }
                                ]
                            },
                        },
                        {
                            bool: {
                                must: [
                                    { term: { restrictionType: 'Local' } },
                                    { term: { restrictedSites: window.s.site.id } },
                                ],
                            },
                        },
                    ],
                },
            }),
            new: () => ({
                bool: {
                    should: [
                        {
                            bool: {
                                must: {
                                    match: {
                                        isNew: 1
                                    }
                                }
                            }
                        },
                        {
                            bool: {
                                must: {
                                    range: {
                                        validFrom: {
                                            gt: 'now-' + NEW_THRESHOLD + 'd/d',
                                            lte: 'now/d',
                                        },
                                    },
                                }
                            },
                        }
                    ],
                },
            }),
            onlineOnly: () => ({
                bool: {
                    must: [
                        {
                            exists: { field: "redemptions.online" }
                        },
                        {
                            bool: {
                                must_not: {
                                    exists: {
                                        field: "redemptions.instore"
                                    }
                                }
                            }
                        },
                        {
                            bool: {
                                must_not: {
                                    exists: {
                                        field: "redemptions.mobile"
                                    }
                                }
                            }
                        },
                        {
                            bool: {
                                must_not: {
                                    exists: {
                                        field: "redemptions.email"
                                    }
                                }
                            }
                        }
                    ]
                }
            }),
            child: () => ({
                bool: {
                    must_not: {
                        term: {
                            hideFromChildUser: 1,
                        },
                    },
                },
            }),
            banner: () => [
                {
                    term: { tier: 1 },
                },
                {
                    exists: {
                        field: 'images.heroBanner',
                    },
                },
            ],
            express: () => ({
                term: {
                    isExpress: 1
                }
            }),
            location: () => ({
                bool: {
                    should: [
                        {
                            bool: {
                                must: [
                                    {
                                        term: {
                                            redemptions: "instore"
                                        }
                                    },
                                    {
                                        bool: {
                                            must_not: {
                                                terms: {
                                                    redemptions: ["online", "mobile", "email"]
                                                }
                                            }
                                        }
                                    },
                                    {
                                        geo_distance: {
                                            distance: this.distance + ' ' + this.distanceUnit,
                                            distance_type: "arc",
                                            "locations.pin": {
                                                lat: parseFloat(this.location.latitude),
                                                lon: parseFloat(this.location.longitude)
                                            }
                                        }
                                    }
                                ]
                            }
                        },
                        {
                            bool: {
                                must_not: {
                                    bool: {
                                        must: [
                                            {
                                                term: {
                                                    redemptions: "instore"
                                                }
                                            },
                                            {
                                                bool: {
                                                    must_not: {
                                                        terms: {
                                                            redemptions: ["online", "mobile", "email"]
                                                        }
                                                    }
                                                }
                                            }
                                        ]
                                    }
                                },
                                should: [
                                    {
                                        bool: {
                                            must_not: [
                                                {
                                                    exists: {
                                                        field: "locations"
                                                    }
                                                }
                                            ]
                                        }
                                    },
                                    {
                                        bool: {
                                            must: [
                                                {
                                                    exists: {
                                                        field: "locations"
                                                    }
                                                },
                                                {
                                                    term: {
                                                        provinces: this.location.province
                                                    }
                                                }
                                            ]
                                        }
                                    }
                                ]
                            }
                        }
                    ]
                }
            }),
            closeBy: () => ({
                bool: {
                    should: [
                        {
                            bool: {
                                must_not: { exists: { field: 'locations' } },
                            },
                        },
                        {
                            geo_distance: {
                                distance:
                                    this.distance + ' ' + this.distanceUnit,
                                distance_type: 'arc',
                                'locations.pin': {
                                    lat: parseFloat(this.location.latitude),
                                    lon: parseFloat(this.location.longitude),
                                },
                            },
                        },
                    ],
                },
            }),
            redemptionType: (redemption) => ({
                bool: {
                    must: {
                        terms: { redemptions: redemption }
                    }
                },
            }),
            ids: perkIds => ({
                ids: {
                    values: perkIds,
                },
            }),
            accountIds: accountIds => ({
                accountId: {
                    values: accountIds,
                },
            }),
        }
    }
}
